import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ChevronRight, FolderKanban, MessageSquare, Languages, Bell } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { AdminTabs, AdminTabsHint } from "../../components/AdminTabs";
import { STATUSES, PRIORITY_STYLE, langName } from "../../lib/constants";
import { useLang } from "../../context/LanguageContext";
import { priorityLabel, statusLabel } from "../../lib/statusI18n";
import ProjectWorkspace from "../../components/admin/ProjectWorkspace";

// « Projets par utilisateur » : chaque candidat est regroupé avec une pastille du nombre
// de projets ; un clic déroule tous ses dossiers et un clic sur un dossier ouvre
// l'espace de travail COMPLET en plein écran — sans jamais quitter cette section.
// Affichage instantané + auto-réparation. Pastille rouge = nouveaux messages du client.
const CACHE_KEY = "aca_admin_byuser_cache";

export default function AdminUserProjects() {
  const { tf: t } = useLang();
  const [groups, setGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "[]"); } catch { return []; }
  });
  const [loading, setLoading] = useState(() => !localStorage.getItem(CACHE_KEY));
  const [retrying, setRetrying] = useState(false);
  const [open, setOpen] = useState({});
  const [activePid, setActivePid] = useState(null);
  const [statusFilter, setStatusFilter] = useState(""); // filtre par statut (onglets colorés)
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const initials = (name) => (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  const RETRY_DELAYS = [3, 4, 5, 6, 8, 10, 12, 15, 15, 20, 20, 20, 25, 30, 30];
  const load = (attempt = 0) => {
    api.get("/admin/projects/by-user")
      .then((r) => {
        setGroups(r.data);
        setRetrying(false);
        setLoading(false);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(r.data)); } catch { /* quota */ }
      })
      .catch(() => {
        if (attempt < RETRY_DELAYS.length) {
          setRetrying(true); setLoading(false);
          setTimeout(() => load(attempt + 1), RETRY_DELAYS[attempt] * 1000);
        } else { setRetrying(false); setLoading(false); toast.error("Chargement impossible pour le moment — réessayez dans une minute."); }
      });
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Ouverture directe d'un dossier demandée depuis une autre page admin
  // (ex. fiche utilisateur → « Projets & dossiers ») : l'espace de travail
  // s'ouvre ici, JAMAIS dans le suivi des demandes.
  const location = useLocation();
  useEffect(() => {
    const pid = location.state?.openPid;
    if (pid) {
      setActivePid(pid);
      window.history.replaceState({}, "");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // L'espace de travail recouvre tout l'écran : on bloque le défilement de la liste dessous.
  useEffect(() => {
    if (activePid) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [activePid]);

  const totalProjects = groups.reduce((n, g) => n + g.projects.length, 0);
  const totalUnread = groups.reduce((n, g) => n + g.projects.reduce((m, p) => m + (p.unread || 0), 0), 0);
  // Résumé des statuts pour les candidats à plusieurs projets (ex. « 1 Sélectionnée · 2 Idée déposée »).
  const statusSummary = (projects) => {
    const counts = {};
    projects.forEach((p) => { counts[p.status] = (counts[p.status] || 0) + 1; });
    return Object.entries(counts).map(([s, n]) => `${n} ${statusLabel(t, s)}`).join(" · ");
  };

  // ---------- Onglets de filtre par statut ----------
  const STATUS_TAB_COLORS = {
    "BROUILLON": "blue", "IDÉE DÉPOSÉE": "cyan", "RETENU PAR NOS ÉQUIPES": "green",
    "NON RETENU PAR NOS ÉQUIPES": "rose", "EN ÉTUDE": "amber", "PRÉSÉLECTIONNÉE": "violet",
    "FINALISTE": "violet", "SÉLECTIONNÉE": "green", "EN CONCEPTION": "cyan",
    "EN DESIGN": "cyan", "EN DÉVELOPPEMENT": "cyan", "EN TEST": "amber", "APPLICATION TERMINÉE": "green",
  };
  const statusCounts = {};
  groups.forEach((g) => g.projects.forEach((p) => { statusCounts[p.status] = (statusCounts[p.status] || 0) + 1; }));
  const filterTabs = STATUSES.filter((s) => statusCounts[s]).map((s) => ({
    key: s, label: `${statusLabel(t, s)} (${statusCounts[s]})`, color: STATUS_TAB_COLORS[s] || "cyan",
  }));
  // Groupes visibles : seuls les projets du statut choisi restent affichés.
  const visibleGroups = groups
    .map((g) => ({ ...g, projects: statusFilter ? g.projects.filter((p) => p.status === statusFilter) : g.projects }))
    .filter((g) => g.projects.length > 0);
  // Filtre actif → les groupes correspondants se déplient automatiquement.
  useEffect(() => {
    if (statusFilter) {
      const o = {};
      groups.forEach((g) => { if (g.projects.some((p) => p.status === statusFilter)) o[g.user_id] = true; });
      setOpen(o);
    }
  }, [statusFilter, groups]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mx-auto max-w-5xl">
      <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">Demandes</div>
      <h1 className="mt-3 font-display text-3xl font-900 text-white">Projets par utilisateur</h1>
      <p className="mt-2 text-sm text-ink-secondary">
        {groups.length} candidat{groups.length > 1 ? "s" : ""} · {totalProjects} projet{totalProjects > 1 ? "s" : ""} — cliquez sur un utilisateur pour dérouler tous ses dossiers, puis sur un dossier pour tout gérer sans quitter cette section.
      </p>

      {totalUnread > 0 && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-red-400/40 bg-red-500/10 px-4 py-2.5 text-xs font-600 text-red-300" data-testid="byuser-unread-total">
          <Bell size={13} className="animate-pulse" /> {totalUnread} message{totalUnread > 1 ? "s" : ""} de client{totalUnread > 1 ? "s" : ""} en attente de lecture
        </div>
      )}

      {retrying && (
        <div className="mt-4 rounded-xl border border-cyan/30 bg-cyan/[0.06] px-4 py-2.5 text-center text-xs text-cyan" data-testid="byuser-retrying">
          Le serveur redémarre (mise en ligne en cours) — nouvelle tentative automatique dans quelques secondes…
        </div>
      )}

      {/* Onglets de filtre par statut — pour bien dissocier les projets */}
      {filterTabs.length > 0 && (
        <>
          <AdminTabs testid="byuser-status-tabs" tab={statusFilter}
            onToggle={(k) => setStatusFilter((cur) => (cur === k ? "" : k))} tabs={filterTabs} />
          <AdminTabsHint testid="byuser-status-tabs-hint">
            {statusFilter
              ? `Filtre actif : ${statusLabel(t, statusFilter)} — recliquez sur l'onglet pour tout réafficher.`
              : "Cliquez sur un statut pour filtrer les projets — les dossiers correspondants se déplient automatiquement."}
          </AdminTabsHint>
        </>
      )}

      <div className="mt-6 space-y-3" data-testid="byuser-list">
        {visibleGroups.map((g) => {
          const groupUnread = g.projects.reduce((m, p) => m + (p.unread || 0), 0);
          return (
          <div key={g.user_id} className="overflow-hidden rounded-xl border border-white/10" data-testid={`byuser-group-${g.user_id}`}>
            <button onClick={() => toggle(g.user_id)} data-testid={`byuser-toggle-${g.user_id}`}
              title={open[g.user_id] ? "Masquer ses dossiers" : "Voir tous ses dossiers"}
              className="flex w-full items-center gap-3 bg-surface/60 px-4 py-3.5 text-left transition-colors hover:bg-cyan/5">
              <ChevronRight size={16} className={`shrink-0 text-cyan transition-transform duration-200 ${open[g.user_id] ? "rotate-90" : ""}`} />
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cyan/15 text-sm font-700 text-cyan">{initials(g.name)}</span>
              <div className="min-w-0">
                <div className="truncate text-sm font-600 text-white">{g.name}</div>
                <div className="truncate text-xs text-ink-tertiary">{g.email}{g.country ? ` · ${g.country}` : ""}</div>
                {/* Statut du projet directement sur la ligne : badge (1 projet) ou résumé (plusieurs) */}
                {g.projects.length > 1 && (
                  <div className="mt-0.5 truncate text-[10px] font-600 text-cyan/80" data-testid={`byuser-status-summary-${g.user_id}`}>{statusSummary(g.projects)}</div>
                )}
              </div>
              {g.projects.length === 1 && <StatusBadge status={g.projects[0].status} className="ml-auto shrink-0" />}
              {groupUnread > 0 && (
                <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border border-red-400/40 bg-red-500/15 px-2.5 py-1 text-[10px] font-700 text-red-300 ${g.projects.length === 1 ? "" : "ml-auto"}`} data-testid={`byuser-group-unread-${g.user_id}`}>
                  <Bell size={10} /> {groupUnread}
                </span>
              )}
              <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-700 ${g.projects.length > 1 ? "border-cyan/40 bg-cyan/10 text-cyan" : "border-white/15 text-ink-secondary"} ${g.projects.length === 1 || groupUnread > 0 ? "" : "ml-auto"}`}
                data-testid={`byuser-count-${g.user_id}`}>
                <FolderKanban size={12} /> {g.projects.length} projet{g.projects.length > 1 ? "s" : ""}
              </span>
            </button>

            {open[g.user_id] && (
              <div className="divide-y divide-white/5 border-t border-white/10">
                {g.projects.map((p) => (
                  <button key={p.id} onClick={() => setActivePid(p.id)} data-testid={`byuser-project-${p.id}`}
                    title={`Ouvrir et gérer le dossier ${p.ref} (détail, échanges, production) — sans quitter cette section`}
                    className="flex w-full items-center gap-3 px-4 py-3 pl-14 text-left transition-colors hover:bg-cyan/5">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-600 text-white">{p.app_name || p.title}</span>
                        {(p.unread || 0) > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-red-400/40 bg-red-500/15 px-2 py-0.5 text-[9px] font-700 uppercase tracking-wider text-red-300" data-testid={`byuser-unread-${p.id}`}>
                            <Bell size={9} /> {p.unread} nouveau{p.unread > 1 ? "x" : ""}
                          </span>
                        )}
                        {p.lang && p.lang !== "fr" && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-cyan/30 bg-cyan/10 px-2 py-0.5 text-[9px] font-700 uppercase tracking-wider text-cyan">
                            <Languages size={10} /> {langName(p.lang)} → FR
                          </span>
                        )}
                        {p.priority && <span className={`border px-2 py-0.5 text-[10px] font-600 uppercase tracking-wider ${PRIORITY_STYLE[p.priority]}`}>{priorityLabel(t, p.priority)}</span>}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-[11px] text-ink-tertiary">
                        <span>{p.ref}</span>
                        {p.category && <span>{p.category}</span>}
                        <span className="inline-flex items-center gap-1"><MessageSquare size={10} /> {p.messages_count} échange{p.messages_count > 1 ? "s" : ""}</span>
                        {p.status === "SÉLECTIONNÉE" && <span className="text-cyan">{p.prod_status || "Sélectionné"} · {p.progress || 0}%</span>}
                      </div>
                    </div>
                    <StatusBadge status={p.status} />
                    <ChevronRight size={15} className="shrink-0 text-ink-tertiary" />
                  </button>
                ))}
              </div>
            )}
          </div>
          );
        })}
        {!loading && !retrying && groups.length === 0 && (
          <div className="rounded-xl border border-white/10 p-12 text-center text-sm text-ink-secondary" data-testid="byuser-empty">Aucune demande déposée pour le moment.</div>
        )}
      </div>

      {/* Espace de travail plein écran : on reste dans « Projets par utilisateur ».
          data-lenis-prevent : la molette garde son défilement natif ici (Lenis gère la page). */}
      {activePid && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-void" data-testid="byuser-workspace" data-lenis-prevent>
          <div className="px-4 py-6 md:px-8">
            <ProjectWorkspace pid={activePid} backLabel="Projets par utilisateur" onBack={() => { setActivePid(null); load(); }} />
          </div>
        </div>
      )}
    </div>
  );
}
